import { call, put, select, takeLatest } from 'typed-redux-saga'
import {
	errorHandler,
	NotificationPopupFooterComponent,
	NOTIFICATION_TYPE,
	notificationPopupSlice,
	notifyMessageCreator,
	modalsSlice,
	NotificationPopupModel,
	loadersSlice,
	formsSelectors,
} from 'Modules'
import { Deserialize } from 'cerialize'
import { MODAL_NAME } from 'Constants/ModalName'
import { FormNames } from 'Constants/FormNames'
import {
	BgLeadNs,
	bgRequestSlice,
	bgRequestSelector,
	bgLimitRequestSelector,
	bgLimitRequestSlice,
	getLimitRequestItem,
	bgLeadSlice,
	RequestType,
	bgLeadApi,
	bgLimitRequestLeadApi,
	BgLimitRequestFull,
	BankGuaranteeFull,
	LimitMessage,
	NotificationMessageComponent,
	bgSignatorySlice,
	DirectorLeadFIO,
	LeadFIO,
	DirectorForm,
	DefaultForm,
	leaderFieldsWithoutFIO,
} from 'Pages/ibro'
import { authSelector } from 'Pages/auth'
import { sagaCallbackChannel } from 'Store'
import { routerActions } from 'connected-react-router'
import { FrontUrls } from 'Constants/FrontUrls'

let task

function* changeLead(action: BgLeadNs.ChangeLead) {
	try {
		const { id, lead_id, requestType, changeOptions } = action.payload
		yield* put(loadersSlice.actions.showGlobalLoader())
		const changeLead =
			requestType === RequestType.LIMIT_REQUEST
				? bgLimitRequestLeadApi.limitChangeLead
				: bgLeadApi.changeLead
		const res = yield* call(changeLead, id, lead_id, changeOptions)

		if (requestType === RequestType.LIMIT_REQUEST) {
			const currentBg = yield* select(bgLimitRequestSelector.selectCurrentLimitRequest)
			if (currentBg?.id === res?.id) {
				const bg = Deserialize(res, BgLimitRequestFull)
				yield* put(bgLimitRequestSlice.actions.setLimitRequest({ data: bg }))
			}
		} else {
			const currentBg = yield* select(bgRequestSelector.selectCurrentBg)
			if (currentBg?.id === res?.id) {
				const bg = Deserialize(res, BankGuaranteeFull)
				const form = yield* select(
					formsSelectors.getGlobalFormMemoized(FormNames.BROKER.REQUEST.CLIENT_INFO)
				)
				const defaultFormFieldsValue = form.getFieldsValue(leaderFieldsWithoutFIO)
				yield* put(bgRequestSlice.actions.setItem({ data: bg }))
				if (changeOptions.is_director) {
					yield* put(bgSignatorySlice.actions.setDirectorLeadFIO(Deserialize(res, DirectorLeadFIO)))
					yield* put(bgSignatorySlice.actions.setDirectorForm(Deserialize(res, DirectorForm)))

					yield* put(
						bgSignatorySlice.actions.setDefaultForm(
							Deserialize(defaultFormFieldsValue, DefaultForm)
						)
					)
				} else {
					yield* put(bgSignatorySlice.actions.setLeadFIO(Deserialize(res, LeadFIO)))
					yield* put(bgSignatorySlice.actions.setDefaultForm(Deserialize(res, DefaultForm)))
				}
			}
		}
		yield* put(loadersSlice.actions.hideGlobalLoader())
	} catch (error) {
		yield* put(loadersSlice.actions.hideGlobalLoader())
		yield* call(errorHandler, error)
	} finally {
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.CHANGE_LEAD_BG))
	}
}
function* sendConfirmation(action: BgLeadNs.SendConfirmation) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, phone, requestType, attorney, is_director } = action.payload
		if (requestType === RequestType.LIMIT_REQUEST) {
			yield* call(bgLimitRequestLeadApi.limitAgreeConfirmation, requestType, id, phone)
		} else {
			yield* call(bgLeadApi.agreeConfirmation, id, phone, attorney)
		}

		const form = yield* select(
			formsSelectors.getGlobalFormMemoized(FormNames.BROKER.REQUEST.CLIENT_INFO)
		)
		const defaultFormFieldsValue = form?.getFieldsValue(leaderFieldsWithoutFIO)
		if (requestType === RequestType.LIMIT_REQUEST) {
			yield* call(getLimitRequestItem, id)
		} else if (is_director && defaultFormFieldsValue) {
			yield* put(bgRequestSlice.actions.getDirectorItem(id))
			yield* put(
				bgSignatorySlice.actions.setDefaultForm(Deserialize(defaultFormFieldsValue, DefaultForm))
			)
		} else {
			yield* put(bgRequestSlice.actions.getDefaultItem(id))
		}

		const data = [phone]
		yield* call(notifyMessageCreator, 'broker.send_confirmation.success', { data })
	} catch (error) {
		yield* call(errorHandler, error, FormNames.BROKER.AGREEMENT)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* sendPaperLead(action: BgLeadNs.SendPaperLead) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, data, requestType } = action.payload
		if (requestType === RequestType.LIMIT_REQUEST) {
			const { attorney, is_director, ...reqData } = data
			yield* call(bgLimitRequestLeadApi.limitAgreePaperLead, requestType, id, reqData)
		} else {
			yield* call(bgLeadApi.agreePaperLead, id, data)
		}
		const { is_director } = data
		const form = yield* select(
			formsSelectors.getGlobalFormMemoized(FormNames.BROKER.REQUEST.CLIENT_INFO)
		)
		const defaultFormFieldsValue = form?.getFieldsValue(leaderFieldsWithoutFIO)
		if (requestType === RequestType.LIMIT_REQUEST) {
			yield* call(getLimitRequestItem, id)
		} else if (is_director && defaultFormFieldsValue) {
			yield* put(bgRequestSlice.actions.getDirectorItem(id))
			yield* put(
				bgSignatorySlice.actions.setDefaultForm(Deserialize(defaultFormFieldsValue, DefaultForm))
			)
		} else {
			yield* put(bgRequestSlice.actions.getDefaultItem(id))
		}
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.AGREEMENT_BG))
	} catch (error) {
		yield* call(errorHandler, error, FormNames.BROKER.AGREEMENT)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* sendDigitalLead(action: BgLeadNs.SendDigitalLead) {
	try {
		yield* put(loadersSlice.actions.showGlobalLoader())
		const { id, data, requestType } = action.payload
		if (requestType === RequestType.LIMIT_REQUEST) {
			const { attorney, is_director, ...reqData } = data
			yield* call(bgLimitRequestLeadApi.limitAgreeDigitalLead, requestType, id, reqData)
		} else {
			yield* call(bgLeadApi.agreeDigitalLead, id, data)
		}
		const { is_director } = data
		const form = yield* select(
			formsSelectors.getGlobalFormMemoized(FormNames.BROKER.REQUEST.CLIENT_INFO)
		)
		const defaultFormFieldsValue = form?.getFieldsValue(leaderFieldsWithoutFIO)
		if (requestType === RequestType.LIMIT_REQUEST) {
			yield* call(getLimitRequestItem, id)
		} else if (is_director && defaultFormFieldsValue) {
			yield* put(bgRequestSlice.actions.getDirectorItem(id))
			yield* put(
				bgSignatorySlice.actions.setDefaultForm(Deserialize(defaultFormFieldsValue, DefaultForm))
			)
		} else {
			yield* put(bgRequestSlice.actions.getDefaultItem(id))
		}
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.AGREEMENT_BG))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(loadersSlice.actions.hideGlobalLoader())
	}
}

function* checkPersonalDataAgreement(action: BgLeadNs.CheckPersonalDataAgreement) {
	try {
		const partner_id = yield* select(authSelector.getUserPartnerIdSelector)
		const res = yield* call(bgLeadApi.checkPersonalDataAgreement, partner_id, action.payload)
		yield* put(bgRequestSlice.actions.setAgreementStatus(res))
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* leadComplete(action: BgLeadNs.LeadComplete) {
	try {
		const { id, status, requestType, info } = action.payload
		yield* put(modalsSlice.actions.hideModal(MODAL_NAME.AGREEMENT_BG))
		const currentLimitRequest = yield* select(bgLimitRequestSelector.selectCurrentLimitRequest)

		const NotifyLimitReq: NotificationPopupModel = {
			type: status ? NOTIFICATION_TYPE.SUCCESS : NOTIFICATION_TYPE.ERROR,
			title: 'broker.lead_complete',
			btnTitle: 'broker.open_request',
			close: true,
			body: LimitMessage({ name: currentLimitRequest?.name }),
			footer: NotificationPopupFooterComponent({
				title: 'broker.open_request',
				action: () =>
					sagaCallbackChannel.put(
						routerActions.push(FrontUrls.broker.limits_edit.replace(':id', id.toString()))
					),
			}),
		}

		const NotifyReq: NotificationPopupModel = {
			type: status ? NOTIFICATION_TYPE.SUCCESS : NOTIFICATION_TYPE.ERROR,
			title: 'broker.lead_complete',
			close: true,
			body: NotificationMessageComponent(info),
			footer: NotificationPopupFooterComponent({
				title: 'broker.open_request',
				action: () =>
					sagaCallbackChannel.put(
						routerActions.push(FrontUrls.broker.requests_edit.replace(':id', id.toString()))
					),
			}),
		}

		const notifyData: NotificationPopupModel =
			requestType === RequestType.LIMIT_REQUEST ? NotifyLimitReq : NotifyReq
		yield* put(notificationPopupSlice.actions.add(notifyData))

		if (requestType === RequestType.LIMIT_REQUEST && currentLimitRequest) {
			if (info?.inn && currentLimitRequest.id && currentLimitRequest?.inn === info.inn)
				yield* call(getLimitRequestItem, currentLimitRequest.id)
		} else {
			const currentBg = yield* select(bgRequestSelector.selectCurrentBg)
			if (info?.inn && currentBg?.inn === info.inn)
				yield* put(bgRequestSlice.actions.getItem(currentBg.id))
		}
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* cancelCheckPersonalDataAgreement() {
	task.cancel()
	task = yield* takeLatest(
		bgLeadSlice.actions.checkPersonalDataAgreement.type,
		checkPersonalDataAgreement
	)
}

export function* bgLeadSaga() {
	yield* takeLatest(bgLeadSlice.actions.changeLeadBg.type, changeLead)
	yield* takeLatest(bgLeadSlice.actions.sendConfirmation.type, sendConfirmation)
	yield* takeLatest(bgLeadSlice.actions.sendPaperLead.type, sendPaperLead)
	yield* takeLatest(bgLeadSlice.actions.sendDigitalLead.type, sendDigitalLead)
	yield* takeLatest(bgLeadSlice.actions.leadCompleteBg.type, leadComplete)
	task = yield* takeLatest(
		bgLeadSlice.actions.checkPersonalDataAgreement.type,
		checkPersonalDataAgreement
	)
	yield* takeLatest(
		bgLeadSlice.actions.cancelCheckPersonalDataAgreement.type,
		cancelCheckPersonalDataAgreement
	)
}
